'use client';

import type { UseQueryResult } from '@tanstack/react-query';
import {
  createContext,
  type PropsWithChildren,
  useContext,
  useMemo,
} from 'react';

import { useCartContext } from '@/app/contexts/CartProvider';
import { useDiscount } from '@/queries/GetOneDiscount';
import type { ListProduct } from '@/queries/Products';
import { useProductsData } from '@/queries/Products';
import type { GetOneDiscount_content_discount } from '@/queries/types/GetOneDiscount';
import type { RefreshCart_cart_refreshCart_cart_lineItems } from '@/queries/types/RefreshCart';
import type { SavePromotion_cart_savePromotion } from '@/queries/types/SavePromotion';

import useProductsInventory, {
  DEFAULT_PRODUCTS_INVENTORY,
  type UseProductsInventory,
} from '../_hooks/useProductsInventory';
import getPromosFromCart from '../_utils/getPromosFromCart';

interface PDPValues extends UseProductsInventory {
  appliedDiscountCode: string;
  chargerCount: number;
  data?: ListProduct[];
  discount: GetOneDiscount_content_discount | null;
  discountIsLoading: boolean;
  discountIsSuccess: boolean;
  error: unknown;
  isLoadingPdpData: boolean;
  isSuccess: boolean;
  itemCountForIcon: number;
  lineItems: (RefreshCart_cart_refreshCart_cart_lineItems | null)[];
  maxChargerQuantity: number;
  maxRingQuantity: number;
  promotionCodes: (string | null | undefined)[];
  ringCount: number;
  urlPromotionData: SavePromotion_cart_savePromotion | null | undefined;
}

export const INITIAL_PDP_VALUES: PDPValues = {
  appliedDiscountCode: '',
  chargerCount: 0,
  data: [],
  discount: null,
  discountIsLoading: false,
  discountIsSuccess: false,
  error: null,
  isLoadingPdpData: true,
  isSuccess: false,
  itemCountForIcon: 0,
  lineItems: [],
  maxChargerQuantity: 10,
  maxRingQuantity: 3,
  promotionCodes: [],
  ringCount: 0,
  urlPromotionData: null,
  ...DEFAULT_PRODUCTS_INVENTORY,
};

type PDPProvider = PDPValues & UseQueryResult<ListProduct[], Error>;
export type PDPContextType =
  | (PDPProvider & UseProductsInventory)
  | (PDPValues & UseProductsInventory);

const PDPContext = createContext<PDPContextType>(INITIAL_PDP_VALUES);

export const usePDPContext = (): PDPContextType => {
  return useContext(PDPContext);
};

const Provider = ({ children }: PropsWithChildren): JSX.Element => {
  const inventory = useProductsInventory();
  // get cart
  const cartContext = useCartContext();
  const refreshCartQuery = cartContext?.refreshCart;
  const cart = refreshCartQuery?.data?.cart;
  const cartIsLoading = Boolean(refreshCartQuery?.isLoading);
  const cartError = refreshCartQuery?.error;
  const cartSettingsQuery = cartContext?.cartSettings;
  const cartSettings = cartSettingsQuery?.data;
  const cartSettingsIsLoading = Boolean(cartSettingsQuery?.isLoading);
  const cartSettingsError = cartSettingsQuery?.error;
  const urlPromotionData = cartContext?.urlDiscount.data;

  if (cartSettingsError) {
    // eslint-disable-next-line no-console
    console.error('Error fetching cart settings', cartSettingsError);
  }

  if (cartError) {
    // eslint-disable-next-line no-console
    console.error('Error fetching cart', cartError);
  }

  const { appliedDiscountCode, promotionCodes } = getPromosFromCart(cart);

  const hasDiscount = Boolean(appliedDiscountCode);

  // get products
  const products = useProductsData({
    enabled: hasDiscount,
  });

  // get discount
  const {
    data: discountData,
    error: discountError,
    isLoading: discountIsLoading,
    isSuccess: discountIsSuccess,
  } = useDiscount(appliedDiscountCode);

  if (discountError) {
    // eslint-disable-next-line no-console
    console.error('Error fetching discount data', discountError);
  }

  const isLoadingPdpData =
    cartSettingsIsLoading ||
    cartIsLoading ||
    discountIsLoading ||
    products.isLoading;

  const values: PDPProvider = useMemo(() => {
    const { chargerCount, itemCountForIcon, lineItems, ringCount } =
      cart ?? INITIAL_PDP_VALUES;

    const { maxChargerQuantity, maxRingQuantity } =
      cartSettings ?? INITIAL_PDP_VALUES;

    const discount = discountData?.discount ?? INITIAL_PDP_VALUES.discount;

    return {
      appliedDiscountCode,
      chargerCount: chargerCount!,
      discount,
      discountIsLoading,
      discountIsSuccess,
      itemCountForIcon: itemCountForIcon!,
      lineItems: lineItems!,
      maxChargerQuantity: maxChargerQuantity,
      maxRingQuantity: maxRingQuantity,
      promotionCodes,
      ringCount: ringCount!,
      isLoadingPdpData,
      urlPromotionData,
      ...products,
      ...inventory,
    };
  }, [
    appliedDiscountCode,
    cart,
    cartSettings,
    discountData?.discount,
    discountIsLoading,
    discountIsSuccess,
    inventory,
    isLoadingPdpData,
    products,
    promotionCodes,
    urlPromotionData,
  ]);

  return <PDPContext.Provider value={values}>{children}</PDPContext.Provider>;
};

export default Provider;
